import { useState } from 'react';
import { useAuth } from '@clerk/react';
import useCurrentUser from '../hooks/useCurrentUser.jsx';
import { EmojiDropdown } from '@code-burrow/design-system';

function getApiBase() {
    const proxyUrl = typeof import.meta !== 'undefined' && import.meta.env?.VITE_PROXY_URL;
    return proxyUrl ? `${proxyUrl.replace(/\/+$/, '')}/api` : '/api';
}

/**
 * ProfileSettings — lets the current user update their display name and emoji.
 * Rendered inside a CollapsibleSection in SettingsPage.
 */
export default function ProfileSettings() {
    const currentUser = useCurrentUser();
    const { getToken } = useAuth();

    const [displayName, setDisplayName] = useState(currentUser?.displayName || '');
    const [emoji, setEmoji] = useState(currentUser?.emoji || '');
    const [busy, setBusy] = useState(false);
    const [saved, setSaved] = useState(false);
    const [error, setError] = useState(null);

    const handleSave = async () => {
        if (!displayName.trim()) return;
        setBusy(true);
        setError(null);
        setSaved(false);
        try {
            const token = getToken ? await getToken() : null;
            const res = await fetch(`${getApiBase()}/auth/profile`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    ...(token ? { Authorization: `Bearer ${token}` } : {}),
                },
                body: JSON.stringify({ displayName: displayName.trim(), emoji }),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(data.error || `Failed: ${res.status}`);
            setSaved(true);
            setTimeout(() => setSaved(false), 3000);
        } catch (err) {
            setError(err.message);
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="space-y-4">
            <div>
                <label className="block mb-1 text-xs text-text-tertiary font-body">Display Name</label>
                <input
                    type="text"
                    value={displayName}
                    onChange={(e) => { setDisplayName(e.target.value); setSaved(false); }}
                    maxLength={50}
                    className="w-full px-3 py-2 rounded-md bg-bg-primary border border-border text-text-primary text-sm placeholder:text-text-tertiary focus:outline-none focus:border-accent-primary focus:ring-1 focus:ring-accent-primary"
                />
            </div>
            <div>
                <label className="block mb-1 text-xs text-text-tertiary font-body">Emoji Avatar</label>
                <EmojiDropdown value={emoji} onChange={(e) => { setEmoji(e); setSaved(false); }} placeholder="Pick emoji" />
            </div>
            {error && <p className="text-xs text-red-400">{error}</p>}
            <button
                onClick={handleSave}
                disabled={busy || !displayName.trim()}
                className="px-4 py-2 rounded-md text-sm font-medium bg-accent-primary text-text-inverse hover:bg-accent-primary-hover disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
                {busy ? 'Saving…' : saved ? 'Saved!' : 'Save Changes'}
            </button>
        </div>
    );
}
